import Loading from '@/components/Loading';
import { ChainId, NETWORK_CONFIG, Token } from '@/constant';
import { useAddToken } from '@/hooks/useAddToken';
import application from '@/stores/application';
import user from '@/stores/user';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { observer } from 'mobx-react-lite';
import { AlertTriangle } from 'react-feather';
import styled from 'styled-components';
import { Link, TYPE } from '../../theme';
import CButton from '../Button';
import { AutoColumn } from '../Column';
import Modal from '../Modal';
const Wrapper = styled.div`
    width: 100%;
`;
const Section = styled(AutoColumn)`
    /* padding: 24px; */
`;

const Button = styled(CButton)`
    background: #3563f3 !important;
    color: #fff !important;
    width: 100% !important;
    border-radius: 12px !important;

    &.outline {
        background: #fff !important;
        color: #3563f3 !important;
        border: 1px solid #3563f3 !important;
    }
`;

const BottomSection = styled(Section)`
    /* background-color: #ccc; */
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
`;

const AddToken = styled.div`
    border-radius: 12px;
    padding: 14px;
    box-sizing: border-box;
    background: #fff;
    font-size: 14px;
    color: #000;
    display: flex;
    align-items: center;
    margin-top: 1rem;
    cursor: pointer;
    .metamaskLogo {
        margin-right: 8px;
        width: 20px;
        height: 20px;
    }
`;

const StyleLoading = styled(Loading)`
    margin: 20px 0;
    color: #000;
`;

function ConfirmationPendingContent({
    onDismiss,
    pendingText,
}: {
    onDismiss: () => void;
    pendingText: string;
}) {
    return (
        <Wrapper>
            <Section>
                <AutoColumn gap="12px" justify={'center'}>
                    <TYPE.main fontWeight={500} fontSize={20}>
                        Waiting For Confirmation
                    </TYPE.main>
                    <StyleLoading size={64} />
                    <AutoColumn gap="12px" justify={'center'}>
                        <TYPE.main
                            fontWeight={600}
                            fontSize={14}
                            textAlign="center"
                            color="#000"
                        >
                            {pendingText}
                        </TYPE.main>
                    </AutoColumn>
                    <TYPE.main fontSize={14} color="#000" textAlign="center">
                        Confirm this transaction in your wallet
                    </TYPE.main>
                </AutoColumn>
            </Section>
        </Wrapper>
    );
}

const ImageWrapper = styled.div`
    width: 100%;
    position: relative;
    height: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;

    svg {
        width: 128px;
        height: auto;
        color: #3563f3;
    }

    ${({ theme }) => theme.mediaWidth.upToSmall`
    height: 230px;
    `};
`;

const TextWrapper = styled(AutoColumn)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    flex-direction: column;
`;

function TransactionSubmittedContent({
    onDismiss,
    chainId,
    hash,
    addTokenToMetamask,
    summery,
}: {
    onDismiss: () => void;
    hash: string | undefined;
    chainId: ChainId;
    addTokenToMetamask?: Token;
    summery?: string;
}) {
    const [canAdd, addToMetaMask] = useAddToken(addTokenToMetamask);

    return (
        <Wrapper>
            <Section>
                <ImageWrapper>
                    <CheckCircleIcon />
                </ImageWrapper>
                <TextWrapper>
                    <TYPE.main fontWeight={500} fontSize={20}>
                        Transaction Submitted
                    </TYPE.main>
                    {summery && (
                        <AutoColumn gap="12px" justify={'center'}>
                            <TYPE.main
                                fontWeight={600}
                                fontSize={14}
                                textAlign="center"
                                color="#000"
                            >
                                {summery}
                            </TYPE.main>
                        </AutoColumn>
                    )}

                    {chainId && hash && (
                        <Link
                            color="#000"
                            href={`${NETWORK_CONFIG[chainId].explore}tx/${hash}`}
                        >
                            <TYPE.main
                                fontWeight={500}
                                fontSize={14}
                                color="#000"
                            >
                                View on {NETWORK_CONFIG[chainId].scanLable}
                            </TYPE.main>
                        </Link>
                    )}
                    {addTokenToMetamask && canAdd && (
                        <Button
                            type="outline"
                            onClick={() => {
                                addToMetaMask();
                            }}
                        >
                            Add {addTokenToMetamask.symbol} to Metamask
                        </Button>
                    )}
                    <Button
                        onClick={onDismiss}
                        style={{ margin: '20px 0 0 0' }}
                    >
                        Close
                    </Button>
                </TextWrapper>
            </Section>
        </Wrapper>
    );
}

export function TransactionErrorContent({
    message,
    onDismiss,
}: {
    message: string;
    onDismiss: () => void;
}) {
    return (
        <Wrapper>
            <Section>
                <AutoColumn
                    style={{ marginTop: 20, padding: '2rem 0' }}
                    gap="24px"
                    justify="center"
                >
                    <AlertTriangle
                        color="red"
                        style={{ strokeWidth: 1.5 }}
                        size={64}
                    />
                    <TYPE.main
                        fontWeight={500}
                        fontSize={16}
                        color="red"
                        style={{
                            textAlign: 'center',
                            width: '85%',
                            wordBreak: 'break-word',
                        }}
                    >
                        {message}
                    </TYPE.main>
                </AutoColumn>
            </Section>
            <BottomSection gap="12px">
                <Button onClick={onDismiss}>Dismiss</Button>
            </BottomSection>
        </Wrapper>
    );
}

function TransactionConfirmationModalWithErrorMessage() {
    const { chainId } = user;
    const {
        confirmModal: {
            isOpen,
            attemptingTxn,
            hash,
            pendingText,
            errorMessage,
            addTokenToMetamask,
            summery,
            title,
        },
    } = application;

    function onDismiss() {
        application.setCloseConfirModalState();
    }

    if (!chainId) return null;

    // confirmation screen
    return (
        <Modal isOpen={isOpen} onClose={onDismiss} title={title}>
            {attemptingTxn ? (
                <ConfirmationPendingContent
                    onDismiss={onDismiss}
                    pendingText={pendingText}
                />
            ) : hash ? (
                <TransactionSubmittedContent
                    chainId={chainId}
                    hash={hash}
                    onDismiss={onDismiss}
                    addTokenToMetamask={addTokenToMetamask}
                    summery={summery}
                />
            ) : isOpen ? (
                <TransactionErrorContent
                    onDismiss={onDismiss}
                    message={
                        errorMessage ||
                        'Something wrong, please try again later'
                    }
                />
            ) : null}
        </Modal>
    );
}

export default observer(TransactionConfirmationModalWithErrorMessage);
